import React from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeSlug from "rehype-slug";
import Text from "@amzn/meridian/text";
import { Icon } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-small";
import chatTokens from "@amzn/meridian-tokens/base/icon/chat";
import { useHelpContent } from "./useHelpContent";
import HelpImage from "./HelpImage";
import "./helpSupport.scss";

/**
 * Help & Support page.
 *
 * Renders a single markdown help document fetched from the backend.
 */
const HelpSupportPage: React.FC = () => {
  const navigate = useNavigate();
  const { content, version, isOffline, error } = useHelpContent();

  // The authored markdown starts with the page title as a level-1 heading.
  // Extract it so the title renders above the content it introduces.
  const nl = content.indexOf("\n");
  const firstLine = (nl === -1 ? content : content.slice(0, nl)).trim();
  const hasTitle = firstLine.startsWith("# ");
  const docTitle = hasTitle ? firstLine.replace(/^#\s+/, "") : "";
  const body = !hasTitle ? content : nl === -1 ? "" : content.slice(nl + 1);

  // Inline-styled renderers so headings/text override Katal's global h1-h6 sizing.
  const mdComponents = {
    h1: ({ node, ...props }: any) => (
      <h1 style={{ fontSize: 20, fontWeight: 700, lineHeight: 1.25, margin: "14px 0 4px" }} {...props} />
    ),
    h2: ({ node, ...props }: any) => (
      <h2 style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.25, margin: "18px 0 6px" }} {...props} />
    ),
    h3: ({ node, ...props }: any) => (
      <h3 style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, margin: "14px 0 4px" }} {...props} />
    ),
    p: ({ node, ...props }: any) => (
      <p style={{ fontSize: 14, lineHeight: 1.5, margin: "6px 0", color: "#3f4b5b" }} {...props} />
    ),
    li: ({ node, ...props }: any) => (
      <li style={{ fontSize: 14, lineHeight: 1.5, margin: "4px 0", color: "#3f4b5b" }} {...props} />
    ),
    img: ({ node, ...props }: any) => <HelpImage src={props.src} alt={props.alt} />,
  };

  return (
    <div className="app-gradient-bg pwa-scrollable help-page">
      {/* App header */}
      <div className="help-header">
        <button
          className="help-back-button"
          onClick={() => navigate("/home")}
          aria-label="Back"
        >
          <Icon tokens={arrowLeftTokens} color="success" />
        </button>
        <Text type="h100" className="help-title">
          HELP &amp; SUPPORT
        </Text>
      </div>

      {/* Page title (version/date is carried in the markdown content itself) */}
      {docTitle && (
        <h1
          style={{ fontSize: 24, fontWeight: 700, lineHeight: "31px", color: "#232f3e", margin: "6px 0" }}
        >
          {docTitle}
        </h1>
      )}
      {isOffline && (
        <div className="help-meta">
          <span className="help-meta-updated">Showing last saved copy (offline)</span>
        </div>
      )}

      {/* Markdown content */}
      <div className="help-content markdown-body">
        {error && !content ? (
          <p style={{ fontSize: 14, lineHeight: 1.5, color: "#3f4b5b" }}>
            Couldn&rsquo;t load help content. Please check your connection and
            try again.
          </p>
        ) : (
          <ReactMarkdown rehypePlugins={[rehypeSlug]} components={mdComponents}>{body}</ReactMarkdown>
        )}
      </div>

      {/* Report an issue entry */}
      <div className="help-report-card">
        <div className="help-report-title">Something wrong or missing?</div>
        <div className="help-report-desc">
          Report a bug, flag a classification, or suggest an improvement. One
          form, goes to the EAP program and product teams.
        </div>
        <button
          className="help-report-button"
          onClick={() => navigate("/help/report")}
        >
          <span className="help-report-btn-icon">
            <Icon tokens={chatTokens} color="inverted" />
          </span>
          Report an issue or share feedback
        </button>
      </div>

      {/* Footer: content pack version */}
      <div className="help-footer">
        <Text type="b100" color="secondary">
          Content pack {version}
        </Text>
        <Text type="b100" color="secondary">
          Co-owned by the EAP program team and product
        </Text>
      </div>
    </div>
  );
};

export default HelpSupportPage;
